import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { getDepartmentId, getTenantId } from 'src/app/services/store-snapshot.service';
import { IdName } from 'src/app/shared/models/id-name';
import { environment } from 'src/environments/environment';
import {
  ICorrectiveActionRequest,
  ICorrectiveActionResponse,
} from '../models/corrective-action.model';
import { IMaintenanceJob } from '../models/maintenance-job.model';
import { IPerformGroupedSpareParts } from '../models/perform-grouped-spare-parts.model';
import {
  IPerformJobRequest,
  IPerformJobResponse,
  IPerformJobDetails,
} from '../models/perform-job-request.model';
import { FormDataService } from '../../shared/attachments/service/form-data.service';

@Injectable({
  providedIn: 'root',
})
export class CorrectiveActionApiService {
  private readonly baseUrl = `${environment.apiUrl}/api`;

  private get url(): string {
    return `${this.baseUrl}/${getTenantId()}/${getDepartmentId()}/CorrectiveActions`;
  }

  private get performUrl(): string {
    return `${this.baseUrl}/${getTenantId()}/${getDepartmentId()}/CorrectiveActionPerforms`;
  }

  constructor(private http: HttpClient) {}

  getCorrectiveActionById(id: string): Observable<ICorrectiveActionResponse> {
    const url = `${this.url}/${id}`;
    return this.http.get<ICorrectiveActionResponse>(url);
  }

  loadCorrectiveActionsForIO(): Observable<IdName[]> {
    const url = `${this.url}/Interconnected`;
    return this.http.get<IdName[]>(url);
  }

  loadCorrectiveActionPerformById(performId: string): Observable<IMaintenanceJob> {
    const url = `${this.performUrl}/${performId}`;
    return this.http.get<IMaintenanceJob>(url);
  }

  getCorrectiveActionPerformDetails(performId: string): Observable<IPerformJobDetails> {
    const url = `${this.performUrl}/${performId}`;
    return this.http.get<IPerformJobDetails>(url);
  }

  loadCorrectiveActionDraftPerform(correctiveActionId: string): Observable<IPerformJobDetails> {
    const url = `${this.performUrl}/${correctiveActionId}/draftPerform`;
    return this.http.get<IPerformJobDetails>(url);
  }

  loadSparePartsForPerform(caId: string): Observable<IPerformGroupedSpareParts> {
    const url = `${this.performUrl}/${caId}/spareParts`;
    return this.http.get<IPerformGroupedSpareParts>(url);
  }

  performCorrectiveAction(request: IPerformJobRequest): Observable<IPerformJobResponse> {
    const formData = FormDataService.mapToFormDataWithSpareParts(request);
    return this.http.post<IPerformJobResponse>(`${this.performUrl}/standard`, formData);
  }

  saveCorrectiveActionDraftPerform(request: IPerformJobRequest): Observable<void> {
    const formData = FormDataService.mapToFormDataWithSpareParts(request);
    return this.http.post<void>(`${this.performUrl}/draftperform`, formData);
  }

  createCorrectiveAction(
    correctiveAction: ICorrectiveActionRequest
  ): Observable<ICorrectiveActionResponse> {
    const formData = FormDataService.mapToFormData(correctiveAction);
    return this.http.post<ICorrectiveActionResponse>(this.url, formData);
  }

  updateCorrectiveAction(
    correctiveAction: ICorrectiveActionRequest
  ): Observable<ICorrectiveActionResponse> {
    const formData = FormDataService.mapToFormData(correctiveAction);
    return this.http.put<ICorrectiveActionResponse>(this.url, formData);
  }

  deleteCorrectiveAction(id: string): Observable<void> {
    const url = `${this.url}/${id}`;
    return this.http.delete<void>(url);
  }

  printCorrectiveAction(id: string): Observable<Blob> {
    const url = `${this.url}/${id}/Print`;
    return this.http.get(url, { responseType: 'blob' });
  }
}
